import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { signInLinkPath } from '../../lib/qrLink';
import { useT, type MessageKey } from '../../i18n';

// Profile → My devices → Scan to sign in (M49, D158): reads the computer's
// code with the camera, inside Twinden. Only this site's /link/<code> links
// are followed.

const SCAN_EVERY_MS = 250;

type Detector = {
  detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
};
type DetectorClass = new (options: { formats: string[] }) => Detector;

/** Reads a QR code from one video frame, with whichever decoder we have. */
async function makeReader(): Promise<
  (video: HTMLVideoElement, canvas: HTMLCanvasElement) => Promise<string | null>
> {
  const Native = (window as unknown as { BarcodeDetector?: DetectorClass })
    .BarcodeDetector;
  if (Native) {
    try {
      const detector = new Native({ formats: ['qr_code'] });
      return async (video) => {
        const codes = await detector.detect(video);
        return codes[0]?.rawValue ?? null;
      };
    } catch {
      // Present but without QR support: fall back to jsQR.
    }
  }
  const { default: jsQR } = await import('jsqr');
  return async (video, canvas) => {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(video, 0, 0, width, height);
    const image = context.getImageData(0, 0, width, height);
    return jsQR(image.data, width, height)?.data ?? null;
  };
}

export function QrScanner({
  isOpen,
  onClose,
  onFound,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** The `/link/<code>` path of a scanned sign-in code. */
  onFound: (path: string) => void;
}) {
  const t = useT();
  // The modal mounts its body a moment after opening, so the camera starts
  // once the <video> is there.
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [problem, setProblem] = useState<MessageKey | null>(null);
  const [notCode, setNotCode] = useState(false);
  const found = useRef(onFound);
  found.current = onFound;

  useEffect(() => {
    if (isOpen) return;
    setProblem(null);
    setNotCode(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !video) return;
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    const canvas = document.createElement('canvas');

    const stop = () => {
      stopped = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
      stream = null;
    };

    void (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setProblem('account.scanner.noCamera');
          return;
        }
        const media = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (stopped) {
          // Closed while the browser was asking.
          media.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = media;
        video.srcObject = media;
        await video.play().catch(() => undefined);
        const read = await makeReader();
        if (stopped) return;

        const tick = async () => {
          if (stopped) return;
          const text = await read(video, canvas).catch(() => null);
          if (stopped) return;
          if (text) {
            const path = signInLinkPath(text, window.location.origin);
            if (path) {
              stop();
              found.current(path);
              return;
            }
            setNotCode(true);
          }
          timer = window.setTimeout(() => void tick(), SCAN_EVERY_MS);
        };
        void tick();
      } catch (err) {
        if (stopped) return;
        const name = err instanceof Error ? err.name : '';
        setProblem(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'account.scanner.denied'
            : 'account.scanner.noCamera'
        );
      }
    })();

    return () => {
      stop();
      video.srcObject = null;
    };
  }, [isOpen, video]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={4} borderRadius="28px">
        <ModalHeader>
          <Heading as="h2" fontSize="22px">
            {t('account.scanner.title')}
          </Heading>
        </ModalHeader>
        <ModalBody>
          {problem ? (
            <Alert status="warning" borderRadius="xl">
              <AlertIcon />
              {t(problem)}
            </Alert>
          ) : (
            <VStack spacing={3}>
              <Box
                w="full"
                maxW="320px"
                aspectRatio={1}
                borderRadius="xl"
                overflow="hidden"
                bg="black"
              >
                <video
                  ref={setVideo}
                  muted
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Text textAlign="center" color={notCode ? 'danger' : 'muted'}>
                {notCode
                  ? t('account.scanner.notCode')
                  : t('account.scanner.hint')}
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" onClick={onClose}>
            {t('account.scanner.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
